import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Target, Video, ClipboardList, Calendar, Save, DollarSign, Check, BarChart3, AlertTriangle, X, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import PlayerDevelopmentProfile from '@/components/PlayerDevelopmentProfile';
import DevelopmentRadarChart from '@/components/DevelopmentRadarChart';
import CoachAthleteGoals from '@/components/CoachAthleteGoals';
import AIEvaluationAssistant from '@/components/AIEvaluationAssistant';
import PerformanceAIAssistant from '@/components/PerformanceAIAssistant';
import QuickUpdate from '@/components/QuickUpdate';
import DevelopmentHistory from '@/components/DevelopmentHistory';
import SkillScoreStepper from '@/components/SkillScoreStepper';
import ProfileCompletenessBanner from '@/components/ProfileCompletenessBanner';
import PlayerProfileView from '@/components/PlayerProfileView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Related skills for consistency checking
const RELATED_SKILLS = {
  ball_control: ['dribbling', 'first_touch'],
  dribbling: ['ball_control', 'agility', 'balance'],
  passing: ['vision', 'decision_making'],
  first_touch: ['ball_control'],
  finishing: ['shooting', 'decision_making'],
  shooting: ['finishing'],
  speed: ['acceleration', 'agility'],
  acceleration: ['speed', 'explosiveness'],
  agility: ['balance', 'coordination', 'speed'],
  coordination: ['agility', 'balance'],
  balance: ['coordination', 'agility'],
  explosiveness: ['acceleration', 'speed'],
  positioning: ['game_awareness', 'decision_making'],
  decision_making: ['vision', 'game_awareness', 'positioning'],
  vision: ['passing', 'decision_making', 'game_awareness'],
  game_awareness: ['positioning', 'vision', 'decision_making'],
  reaction_time: ['decision_making', 'game_awareness']
};

// All skill keys for technical development
const ALL_SKILL_KEYS = [
  // TECHNICAL
  'ball_control', 'passing', 'dribbling', 'first_touch', 'finishing', 'shooting',
  // PHYSICAL
  'speed', 'acceleration', 'agility', 'coordination', 'balance', 'explosiveness', 'endurance',
  // TACTICAL / IQ
  'positioning', 'decision_making', 'vision', 'game_awareness', 'reaction_time'
];

// Positions by sport
const POSITIONS = {
  soccer: [
    'Goalkeeper', 'Right Back', 'Left Back', 'Center Back', 
    'Defensive Midfielder', 'Central Midfielder', 'Attacking Midfielder',
    'Right Winger', 'Left Winger', 'Striker', 'Forward'
  ]
};

// Initialize techDev with all skills
const initializeTechDev = (existingData) => {
  const result = { ...existingData };
  ALL_SKILL_KEYS.forEach(key => {
    if (!result[key]) {
      result[key] = { status: '', notes: '' };
    }
  });
  return result;
};

const CoachAthleteDetail = () => {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  // V1: Performance Coach mode — locks the Tech Dev tab to Physical-only inputs
  // and renames the tab/section labels. Reads coach_role from auth context.
  const isPerformanceCoach = user?.coach_role === 'private_performance';
  const [loading, setLoading] = useState(true);
  const [athlete, setAthlete] = useState(null);
  const [techDev, setTechDev] = useState(null);
  const [trainingPlan, setTrainingPlan] = useState(null);
  const [videos, setVideos] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [saving, setSaving] = useState(false);
  const [showConsistencyModal, setShowConsistencyModal] = useState(false);
  const [consistencyAlerts, setConsistencyAlerts] = useState([]);
  // V1: which evaluation sources exist for this athlete. Used to drive the
  // cross-coach recruitment banner shown to a Performance Coach who owns the
  // athlete but the profile is missing technical/tactical sources.
  const [evalSources, setEvalSources] = useState({
    has_team: false, has_private_soccer: false, has_private_performance: false,
  });
  // V3 Quick Update — null means "show selector to coach (full vs quick)";
  // 'full' renders the existing V3 assistant, 'quick' renders the new
  // QuickUpdate card. Reset whenever the athlete changes.
  const [updateMode, setUpdateMode] = useState(null);

  useEffect(() => {
    fetchAllData();
  }, [athleteId]);

  const fetchAllData = async () => {
    try {
      const [athleteRes, techRes, planRes, videosRes, sessionsRes, packagesRes, evalsRes] = await Promise.all([
        axios.get(`${API}/athletes/${athleteId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/athletes/${athleteId}/technical-development`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/athletes/${athleteId}/training-plan`, { headers: getAuthHeaders() }).catch(() => ({ data: null })),
        axios.get(`${API}/athletes/${athleteId}/videos`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/athletes/${athleteId}/sessions`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/session-packages`, { headers: getAuthHeaders() }).catch(() => ({ data: [] })),
        axios.get(`${API}/evaluations/athlete/${athleteId}`, { headers: getAuthHeaders() }).catch(() => ({ data: {} })),
      ]);
      setAthlete(athleteRes.data);
      // Initialize techDev with all skills (merging with existing data)
      setTechDev(initializeTechDev(techRes.data || {}));
      setTrainingPlan(planRes.data);
      setVideos(videosRes.data);
      setSessions(sessionsRes.data);
      setPackages(packagesRes.data.filter(p => p.is_active));
      setEvalSources({
        has_team: !!evalsRes.data?.has_team,
        has_private_soccer: !!evalsRes.data?.has_private_soccer,
        has_private_performance: !!evalsRes.data?.has_private_performance,
      });
    } catch (error) {
      toast.error('Failed to load athlete data');
    }
    setLoading(false);
  };

  const handlePlanTypeChange = (planType) => {
    if (planType === 'package') {
      setAthlete({ ...athlete, plan_type: planType, package_id: '', plan_value: 0, sessions_remaining: 0, sessions_total: 0 });
    } else {
      const sessions = planType === 'monthly' ? 4 : 12;
      setAthlete({ 
        ...athlete, 
        plan_type: planType, 
        package_id: '', 
        plan_value: planType === 'monthly' ? 350 : 900, 
        sessions_remaining: sessions, 
        sessions_total: sessions 
      });
    }
  };

  const handlePackageSelect = (pkg) => {
    setAthlete({
      ...athlete,
      package_id: pkg.id,
      plan_value: pkg.price,
      sessions_remaining: pkg.sessions_count,
      sessions_total: pkg.sessions_count
    });
  };

  const saveAthleteInfo = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/athletes/${athleteId}`, {
        name: athlete.name,
        age: athlete.age,
        position: athlete.position,
        sport: athlete.sport,
        plan_type: athlete.plan_type,
        plan_value: athlete.plan_value,
        sessions_remaining: athlete.sessions_remaining,
        sessions_total: athlete.sessions_total,
        plan_start_date: athlete.plan_start_date,
        plan_end_date: athlete.plan_end_date,
        package_id: athlete.package_id,
        payment_status: athlete.payment_status
      }, { headers: getAuthHeaders() });
      toast.success('Athlete updated');
    } catch (error) {
      toast.error('Failed to update athlete');
    }
    setSaving(false);
  };

  // Check for inconsistencies in the evaluation
  const checkConsistency = () => {
    if (!techDev) return [];
    
    const alerts = [];
    const STATUS_SCORE = { 'Focus': 1, 'Developing': 2, 'Proficient': 3 };
    
    // Check each skill against related skills
    Object.entries(RELATED_SKILLS).forEach(([skill, relatedSkills]) => {
      const skillData = techDev[skill];
      if (!skillData?.status) return;
      
      const skillScore = STATUS_SCORE[skillData.status];
      
      relatedSkills.forEach(relatedSkill => {
        const relatedData = techDev[relatedSkill];
        if (!relatedData?.status) return;
        
        const relatedScore = STATUS_SCORE[relatedData.status];
        const diff = Math.abs(skillScore - relatedScore);
        
        // Alert if there's a 2-level difference (e.g., Focus vs Proficient)
        if (diff >= 2) {
          const skillLabel = skill.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const relatedLabel = relatedSkill.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          alerts.push({
            skill1: skillLabel,
            skill1Status: skillData.status,
            skill2: relatedLabel,
            skill2Status: relatedData.status,
            message: `${skillLabel} (${skillData.status}) and ${relatedLabel} (${relatedData.status}) are closely related skills with a significant gap.`
          });
        }
      });
    });
    
    // Remove duplicates (A-B and B-A)
    const seen = new Set();
    return alerts.filter(alert => {
      const key = [alert.skill1, alert.skill2].sort().join('-');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const saveTechDev = async (forcesSave = false) => {
    // Check consistency before saving
    if (!forcesSave) {
      const alerts = checkConsistency();
      if (alerts.length > 0) {
        setConsistencyAlerts(alerts);
        setShowConsistencyModal(true);
        return;
      }
    }

    setSaving(true);
    try {
      if (isPerformanceCoach) {
        // V1: Performance Coach writes directly to the V3 player_evaluations
        // collection via /api/evaluations so the compile pipeline picks it up
        // with coach_role=private_performance. Skipping the legacy
        // technical_development path avoids collisions with the Soccer Coach's
        // record on the same athlete.
        const physicalKeys = ['speed', 'acceleration', 'agility', 'coordination', 'balance', 'explosiveness', 'endurance'];
        const skills = physicalKeys
          .filter((k) => techDev[k]?.status)
          .map((k) => ({
            skill: k,
            level: (techDev[k].status || 'developing').toLowerCase(),
            score: techDev[k]?.score ? Number(techDev[k].score) : undefined,
            note: techDev[k]?.notes || '',
          }));

        await axios.post(
          `${API}/evaluations/?force_save=true`,
          {
            athlete_id: athleteId,
            coach_id: user?.id,
            coach_type: 'private_performance',
            coach_role: 'private_performance',
            coach_name: user?.full_name || '',
            skills,
          },
          { headers: getAuthHeaders() }
        );
        // Refresh source flags so the completeness banner updates immediately
        setEvalSources((s) => ({ ...s, has_private_performance: true }));
        toast.success('Evaluation submitted — profile compiled', {
          description: 'Your physical evaluation has been added to the player profile.',
          action: {
            label: 'View Profile',
            onClick: () => {
              setActiveTab('profile');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            },
          },
        });
      } else {
        await axios.put(`${API}/athletes/${athleteId}/technical-development`, techDev, { headers: getAuthHeaders() });
        toast.success('Evaluation submitted — profile compiled', {
          description: 'Technical development saved and added to the player profile.',
          action: {
            label: 'View Profile',
            onClick: () => {
              setActiveTab('profile');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            },
          },
        });
      }
      setShowConsistencyModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
    setSaving(false);
  };

  // Handle AI suggestions application
  const handleApplyAISuggestions = (suggestions) => {
    setTechDev(prev => {
      const updated = { ...prev };
      
      // Apply technical suggestions
      Object.entries(suggestions.technical || {}).forEach(([key, value]) => {
        if (updated[key] !== undefined) {
          updated[key] = {
            ...updated[key],
            status: value.level,
            score: value.score || getDefaultScoreForLevel(value.level),
            notes: value.note || updated[key]?.notes || ''
          };
        }
      });
      
      // Apply physical suggestions
      Object.entries(suggestions.physical || {}).forEach(([key, value]) => {
        if (updated[key] !== undefined) {
          updated[key] = {
            ...updated[key],
            status: value.level,
            score: value.score || getDefaultScoreForLevel(value.level),
            notes: value.note || updated[key]?.notes || ''
          };
        }
      });
      
      // Apply tactical suggestions
      Object.entries(suggestions.tactical || {}).forEach(([key, value]) => {
        if (updated[key] !== undefined) {
          updated[key] = {
            ...updated[key],
            status: value.level,
            score: value.score || getDefaultScoreForLevel(value.level),
            notes: value.note || updated[key]?.notes || ''
          };
        }
      });
      
      return updated;
    });
  };

  // Score ranges by level
  const SCORE_RANGES = {
    'Focus': { min: 4.0, max: 6.0, label: '4.0-6.0' },
    'Developing': { min: 6.1, max: 8.0, label: '6.1-8.0' },
    'Proficient': { min: 8.1, max: 10.0, label: '8.1-10.0' }
  };

  // Get default score for a level
  const getDefaultScoreForLevel = (level) => {
    const defaults = { 'Focus': 5.0, 'Developing': 7.0, 'Proficient': 8.3 };
    return defaults[level] || 7.0;
  };

  const updateSkillStatus = (skill, status) => {
    setTechDev(prev => ({
      ...prev,
      [skill]: { 
        ...prev[skill], 
        status,
        score: getDefaultScoreForLevel(status)
      }
    }));
  };

  const updateSkillScore = (skill, newScore) => {
    const currentStatus = techDev[skill]?.status;
    const range = SCORE_RANGES[currentStatus];
    if (!range) return;
    
    let score = parseFloat(newScore);
    if (isNaN(score)) return;
    
    // Clamp to valid range
    score = Math.max(range.min, Math.min(range.max, score));
    // Round to 1 decimal
    score = Math.round(score * 10) / 10;
    
    setTechDev(prev => ({
      ...prev,
      [skill]: { ...prev[skill], score }
    }));
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'overdue': return 'bg-red-500/20 text-red-400 border-red-500/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getPlanLabel = () => {
    if (athlete?.plan_type === 'package') {
      const pkg = packages.find(p => p.id === athlete?.package_id);
      return pkg ? pkg.name : 'Package';
    }
    return athlete?.plan_type === 'monthly' ? 'Monthly Plan' : 'Quarterly Plan';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Target },
    { id: 'technical', label: isPerformanceCoach ? 'Physical Dev' : 'Technical Dev', icon: Target },
    { id: 'profile', label: 'Player Profile', icon: BarChart3 },
    { id: 'history', label: 'Dev History', icon: History },
    { id: 'training', label: 'Training Plan', icon: ClipboardList },
    { id: 'videos', label: 'Videos', icon: Video },
    { id: 'sessions', label: 'Sessions', icon: Calendar },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Athlete not found</p>
        <Link to="/coach/athletes"><Button className="mt-4">Back to Athletes</Button></Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden" data-testid="athlete-detail">
      {/* Header */}
      <div className="flex items-center gap-3 sm:gap-4">
        <Link to="/coach/athletes" className="p-2 hover:bg-muted rounded-sm transition-colors shrink-0">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight truncate">{athlete.name}</h1>
          <p className="text-sm text-muted-foreground truncate">{athlete.position} • {athlete.age} years • {athlete.sport}</p>
        </div>
{/* Evaluation button removed - now integrated in Technical tab */}
      </div>

      {/* Plan Summary Card */}
      <div className="bg-card border border-border/40 p-4 sm:p-5 rounded-sm">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase">Plan Type</p>
            <p className="font-semibold">{getPlanLabel()}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Plan Value</p>
            <p className="font-semibold text-primary">${athlete.plan_value?.toFixed(0) || 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Sessions</p>
            <p className="font-semibold">{athlete.sessions_remaining}/{athlete.sessions_total || '-'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Payment Status</p>
            <span className={`inline-block px-2 py-0.5 rounded text-xs capitalize ${getPaymentStatusColor(athlete.payment_status)}`}>
              {athlete.payment_status || 'pending'}
            </span>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase">Plan Dates</p>
            <p className="text-sm">
              {athlete.plan_start_date ? new Date(athlete.plan_start_date).toLocaleDateString() : 'Not set'} 
              {' - '}
              {athlete.plan_end_date ? new Date(athlete.plan_end_date).toLocaleDateString() : 'Not set'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm uppercase tracking-wider whitespace-nowrap transition-colors ${
              activeTab === tab.id ? 'bg-primary text-black' : 'bg-card border border-border hover:bg-muted'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-card border border-border/40 rounded-sm p-4 sm:p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Athlete Information</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Name</label>
                <Input value={athlete.name} onChange={(e) => setAthlete({...athlete, name: e.target.value})} />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Age</label>
                <Input type="number" value={athlete.age} onChange={(e) => setAthlete({...athlete, age: parseInt(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Sport</label>
                <select 
                  value={athlete.sport} 
                  onChange={(e) => setAthlete({...athlete, sport: e.target.value, position: ''})} 
                  className="w-full p-2 bg-transparent border border-border rounded-sm"
                >
                  <option value="soccer" className="bg-card">Soccer</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Position</label>
                <select 
                  value={athlete.position} 
                  onChange={(e) => setAthlete({...athlete, position: e.target.value})} 
                  className="w-full p-2 bg-transparent border border-border rounded-sm"
                >
                  <option value="" className="bg-card">Select Position</option>
                  {POSITIONS[athlete.sport || 'soccer'].map(pos => (
                    <option key={pos} value={pos} className="bg-card">{pos}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Player Development Profile */}
            {techDev && (
              <div className="mt-6">
                <PlayerDevelopmentProfile techDev={techDev} showArchetype={true} />
              </div>
            )}

            {/* Parent Information */}
            {athlete.parent_id && (
              <div className="mt-6">
                <h3 className="font-heading text-lg uppercase mb-3">Parent / Guardian</h3>
                <div className="bg-muted/20 border border-border/40 rounded-sm p-4">
                  {athlete.parent_name ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                          <span className="text-blue-400 font-semibold">{athlete.parent_name[0]}</span>
                        </div>
                        <div>
                          <p className="font-medium">{athlete.parent_name}</p>
                          {athlete.parent_email && (
                            <a href={`mailto:${athlete.parent_email}`} className="text-sm text-primary hover:underline">
                              {athlete.parent_email}
                            </a>
                          )}
                        </div>
                      </div>
                      {athlete.parent_phone && (
                        <p className="text-sm text-muted-foreground ml-13">
                          Phone: <a href={`tel:${athlete.parent_phone}`} className="text-primary">{athlete.parent_phone}</a>
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">Parent account linked but details not available</p>
                  )}
                </div>
              </div>
            )}

            <h3 className="font-heading text-lg uppercase mt-6">Plan Details</h3>
            
            {/* Plan Type Selection */}
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Plan Type</label>
              <div className="flex gap-2">
                {['monthly', 'quarterly', 'package'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => handlePlanTypeChange(type)}
                    className={`flex-1 py-2 px-3 rounded-sm text-sm transition-colors ${
                      athlete.plan_type === type
                        ? 'bg-primary text-black'
                        : 'bg-muted hover:bg-muted/80'
                    }`}
                  >
                    {type === 'monthly' ? 'Monthly' : type === 'quarterly' ? 'Quarterly' : 'Package'}
                  </button>
                ))}
              </div>
            </div>

            {/* Package Selection */}
            {athlete.plan_type === 'package' && (
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Select Package</label>
                {packages.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3 bg-muted/30 rounded-sm">
                    No packages created. <Link to="/coach/packages" className="text-primary hover:underline">Create package</Link>
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {packages.map((pkg) => (
                      <button
                        key={pkg.id}
                        type="button"
                        onClick={() => handlePackageSelect(pkg)}
                        className={`text-left p-3 rounded-sm border transition-colors ${
                          athlete.package_id === pkg.id
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{pkg.name}</span>
                          <span className="text-primary">${pkg.price}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{pkg.sessions_count} sessions</span>
                        {athlete.package_id === pkg.id && <Check className="inline ml-2 text-primary" size={14} />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Value and Sessions for non-package plans */}
            {athlete.plan_type !== 'package' && (
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground">Plan Value ($)</label>
                  <Input type="number" value={athlete.plan_value} onChange={(e) => setAthlete({...athlete, plan_value: parseFloat(e.target.value)})} />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Total Sessions</label>
                  <Input type="number" value={athlete.sessions_total} onChange={(e) => setAthlete({...athlete, sessions_total: parseInt(e.target.value)})} />
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm text-muted-foreground">Sessions Remaining</label>
                <Input type="number" value={athlete.sessions_remaining} onChange={(e) => setAthlete({...athlete, sessions_remaining: parseInt(e.target.value)})} />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Plan Start Date</label>
                <Input type="date" value={athlete.plan_start_date?.split('T')[0] || ''} onChange={(e) => setAthlete({...athlete, plan_start_date: e.target.value})} />
              </div>
              <div>
                <label className="text-sm text-muted-foreground">Plan End Date</label>
                <Input type="date" value={athlete.plan_end_date?.split('T')[0] || ''} onChange={(e) => setAthlete({...athlete, plan_end_date: e.target.value})} />
              </div>
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Payment Status</label>
              <select 
                value={athlete.payment_status || 'pending'} 
                onChange={(e) => setAthlete({...athlete, payment_status: e.target.value})} 
                className="w-full p-2 bg-transparent border border-border rounded-sm"
              >
                <option value="pending" className="bg-card">Pending</option>
                <option value="paid" className="bg-card">Paid</option>
                <option value="overdue" className="bg-card">Overdue</option>
              </select>
            </div>

            <Button onClick={saveAthleteInfo} disabled={saving} className="bg-primary text-black">
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}

        {activeTab === 'technical' && techDev && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">
              {isPerformanceCoach ? 'Physical Development' : 'Technical Development'}
            </h2>

            {/* V1: completeness banner — only renders for Performance Coaches
                who already have a physical evaluation in place but the
                athlete still lacks Soccer Coach and/or Club Coach sources. */}
            {isPerformanceCoach && (
              <ProfileCompletenessBanner
                sources={evalSources}
                athleteName={athlete?.name}
              />
            )}
            
            {/* DEVELOPMENT REVIEW — wraps both the V3 Full Evaluation and
                the V1 Quick Update behind a single entry point.

                Routing rules:
                  • First-time athlete (no existing evaluation) → render V3
                    directly. No selector. Quick Update is NOT offered.
                  • Athletes with an existing evaluation → small "Update mode"
                    pill remembering the coach's last choice, with a Change
                    link to flip between modes. The selector is shown only
                    the first time (no remembered preference) or after the
                    coach explicitly clicks Change.
                  • Performance Coaches still get their dedicated assistant.

                Radar + Player Profile views are gated below this block —
                they only render once the coach has chosen a mode so the
                "old" radar doesn't compete with the assistant for attention.
            */}
            {!isPerformanceCoach && (() => {
              const hasExistingEval = Boolean(
                techDev && (
                  techDev.last_updated_by_type
                    ? true
                    : Object.values(techDev).some((v) => v && typeof v === 'object' && (v.status || v.score))
                )
              );

              if (!hasExistingEval) {
                return (
                  <AIEvaluationAssistant
                    athleteName={athlete?.name}
                    athletePosition={athlete?.position}
                    athleteAge={athlete?.age}
                    onApplySuggestions={handleApplyAISuggestions}
                    getAuthHeaders={getAuthHeaders}
                    coachType={user?.coach_role === 'team' ? 'team' : 'private'}
                  />
                );
              }

              const prefKey = `meybra:dev-review-mode:${user?.id || user?.coach_id || 'anon'}`;
              const persistMode = (mode) => {
                try { localStorage.setItem(prefKey, mode); } catch (_e) { /* ignore */ }
                setUpdateMode(mode);
              };

              // Lazy-load the persisted choice the first time we render
              // a Development Review for an athlete that has an evaluation.
              if (updateMode === null) {
                let remembered = null;
                try { remembered = localStorage.getItem(prefKey); } catch (_e) { /* ignore */ }
                if (remembered === 'full' || remembered === 'quick') {
                  // Apply silently — render the chosen view immediately.
                  // We schedule a state set so subsequent renders use it.
                  setTimeout(() => setUpdateMode(remembered), 0);
                  return null;
                }

                return (
                  <div
                    className="bg-card border border-border/30 rounded-sm p-5 mb-6"
                    data-testid="dev-review-selector"
                  >
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      Development Review
                    </p>
                    <h3 className="font-heading text-lg mb-4">
                      How would you like to review {athlete?.name?.split(' ')[0] || 'this athlete'}?
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <button
                        onClick={() => persistMode('full')}
                        className="text-left bg-muted/40 hover:bg-muted/60 border border-border/40 hover:border-primary/40 rounded-sm p-4 transition-colors"
                        data-testid="dev-review-mode-full"
                      >
                        <p className="text-sm font-bold uppercase tracking-wider mb-1">Full Development Review</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          Reassess the athlete across all development areas and generate a new profile.
                        </p>
                      </button>
                      <button
                        onClick={() => persistMode('quick')}
                        className="text-left bg-primary/5 hover:bg-primary/15 border border-primary/30 hover:border-primary/60 rounded-sm p-4 transition-colors"
                        data-testid="dev-review-mode-quick"
                      >
                        <p className="text-sm font-bold uppercase tracking-wider mb-1 text-primary">Quick Development Update</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          Add a development observation. AI will review the current profile and suggest only the necessary adjustments.
                        </p>
                      </button>
                    </div>
                  </div>
                );
              }

              const lastLabel = updateMode === 'quick' ? 'Quick Development Update' : 'Full Development Review';
              return (
                <>
                  <div
                    className="flex items-center justify-between gap-2 px-3 py-2 mb-3 bg-muted/30 border border-border/30 rounded-sm text-xs"
                    data-testid="dev-review-mode-pill"
                  >
                    <span className="text-muted-foreground">
                      Last used: <span className="text-foreground font-semibold">{lastLabel}</span>
                    </span>
                    <button
                      onClick={() => persistMode(null) || setUpdateMode(null)}
                      className="text-primary uppercase tracking-widest text-[10px] hover:underline shrink-0"
                      data-testid="dev-review-change-mode"
                    >
                      Change
                    </button>
                  </div>
                  {updateMode === 'quick' ? (
                    <QuickUpdate
                      athleteId={athleteId}
                      athleteName={athlete?.name}
                      coachId={user?.coach_id || user?.id}
                      currentEval={{
                        last_updated_at: techDev?.updated_at,
                        last_updated_by_name: techDev?.last_updated_by_name,
                      }}
                      onBack={() => setUpdateMode(null)}
                      onApplied={() => {
                        // Stay in Quick mode but refresh the data so the
                        // updated levels/scores show on the radar below.
                        fetchAllData();
                      }}
                    />
                  ) : (
                    <AIEvaluationAssistant
                      athleteName={athlete?.name}
                      athletePosition={athlete?.position}
                      athleteAge={athlete?.age}
                      onApplySuggestions={handleApplyAISuggestions}
                      getAuthHeaders={getAuthHeaders}
                      coachType={user?.coach_role === 'team' ? 'team' : 'private'}
                    />
                  )}
                </>
              );
            })()}
            {isPerformanceCoach && (
              <PerformanceAIAssistant
                athleteName={athlete?.name}
                athletePosition={athlete?.position}
                athleteAge={athlete?.age}
                onApplySuggestions={handleApplyAISuggestions}
                getAuthHeaders={getAuthHeaders}
              />
            )}
            
            {/* Radar + Profile gated behind a Development Review choice so
                the previous evaluation doesn't compete with the assistant for
                attention. Performance Coaches keep seeing them always (no
                selector exists on that path). */}
            {(isPerformanceCoach || updateMode !== null) && (
              <>
                {/* Radar Chart - Development Visualization */}
                <DevelopmentRadarChart techDev={techDev} />

                {/* Player Development Profile Summary */}
                <PlayerDevelopmentProfile techDev={techDev} />
              </>
            )}
            
            {/* Status Legend with Score Ranges */}
            <div className="bg-muted/30 p-3 rounded-sm">
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <span className="text-muted-foreground">Status (Score Range):</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span> Focus (4.0-6.0)</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400"></span> Developing (6.1-8.0)</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span> Proficient (8.1-10.0)</span>
              </div>
            </div>

            {/* TECHNICAL SKILLS — hidden for Performance Coach */}
            {!isPerformanceCoach && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-heading text-lg uppercase tracking-wider text-emerald-400">TECHNICAL</h3>
                <div className="flex-1 h-px bg-border"></div>
              </div>
              <div className="space-y-4">
                {[
                  { key: 'ball_control', label: 'Ball Control' },
                  { key: 'passing', label: 'Passing' },
                  { key: 'dribbling', label: 'Dribbling' },
                  { key: 'first_touch', label: 'First Touch' },
                  { key: 'finishing', label: 'Finishing' },
                  { key: 'shooting', label: 'Shooting' },
                ].map((skill) => {
                  const currentStatus = techDev[skill.key]?.status;
                  const range = SCORE_RANGES[currentStatus];
                  return (
                    <div key={skill.key} className="border border-border/40 p-3 sm:p-4 rounded-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <p className="font-medium">{skill.label}</p>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          {/* Status buttons */}
                          <div className="flex flex-wrap gap-2">
                            {['Focus', 'Developing', 'Proficient'].map((status) => (
                              <button
                                key={status}
                                onClick={() => updateSkillStatus(skill.key, status)}
                                className={`px-2 sm:px-3 py-1 text-xs rounded-sm uppercase ${
                                  currentStatus === status
                                    ? status === 'Focus' ? 'bg-red-500/20 text-red-400'
                                    : status === 'Developing' ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-green-500/20 text-green-400'
                                    : 'bg-muted hover:bg-muted/80'
                                }`}
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                          {/* Score input */}
                          {currentStatus && range && (
                            <SkillScoreStepper
                              skillKey={skill.key}
                              value={techDev[skill.key]?.score || getDefaultScoreForLevel(currentStatus)}
                              min={range.min}
                              max={range.max}
                              rangeLabel={range.label}
                              onCommit={(k, v) => updateSkillScore(k, v)}
                              dataTestId={`score-input-${skill.key}`}
                            />
                          )}
                        </div>
                      </div>
                      <Textarea
                        placeholder="Coach notes..."
                        value={techDev[skill.key]?.notes || ''}
                        onChange={(e) => setTechDev({...techDev, [skill.key]: {...techDev[skill.key], notes: e.target.value}})}
                        className="bg-transparent w-full"
                      />
                  </div>
                );
                })}
              </div>
            </div>
            )}

            {/* PHYSICAL SKILLS */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-heading text-lg uppercase tracking-wider text-blue-400">PHYSICAL</h3>
                <div className="flex-1 h-px bg-border"></div>
              </div>
              <div className="space-y-4">
                {[
                  { key: 'speed', label: 'Speed' },
                  { key: 'acceleration', label: 'Acceleration' },
                  { key: 'agility', label: 'Agility' },
                  { key: 'coordination', label: 'Coordination' },
                  { key: 'balance', label: 'Balance' },
                  { key: 'explosiveness', label: 'Explosiveness' },
                  { key: 'endurance', label: 'Endurance' },
                ].map((skill) => {
                  const currentStatus = techDev[skill.key]?.status;
                  const range = SCORE_RANGES[currentStatus];
                  return (
                    <div key={skill.key} className="border border-border/40 p-3 sm:p-4 rounded-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <p className="font-medium">{skill.label}</p>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <div className="flex flex-wrap gap-2">
                            {['Focus', 'Developing', 'Proficient'].map((status) => (
                              <button
                                key={status}
                                onClick={() => updateSkillStatus(skill.key, status)}
                                className={`px-2 sm:px-3 py-1 text-xs rounded-sm uppercase ${
                                  currentStatus === status
                                    ? status === 'Focus' ? 'bg-red-500/20 text-red-400'
                                    : status === 'Developing' ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-green-500/20 text-green-400'
                                    : 'bg-muted hover:bg-muted/80'
                                }`}
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                          {currentStatus && range && (
                            <SkillScoreStepper
                              skillKey={skill.key}
                              value={techDev[skill.key]?.score || getDefaultScoreForLevel(currentStatus)}
                              min={range.min}
                              max={range.max}
                              rangeLabel={range.label}
                              onCommit={(k, v) => updateSkillScore(k, v)}
                              dataTestId={`score-input-${skill.key}`}
                            />
                          )}
                        </div>
                      </div>
                      <Textarea
                        placeholder="Coach notes..."
                        value={techDev[skill.key]?.notes || ''}
                        onChange={(e) => setTechDev({...techDev, [skill.key]: {...techDev[skill.key], notes: e.target.value}})}
                        className="bg-transparent w-full"
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* TACTICAL / IQ SKILLS — hidden for Performance Coach */}
            {!isPerformanceCoach && (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-heading text-lg uppercase tracking-wider text-yellow-400">TACTICAL / IQ</h3>
                <div className="flex-1 h-px bg-border"></div>
              </div>
              <div className="space-y-4">
                {[
                  { key: 'positioning', label: 'Positioning' },
                  { key: 'decision_making', label: 'Decision Making' },
                  { key: 'vision', label: 'Vision' },
                  { key: 'game_awareness', label: 'Game Awareness' },
                  { key: 'reaction_time', label: 'Reaction Time' },
                ].map((skill) => {
                  const currentStatus = techDev[skill.key]?.status;
                  const range = SCORE_RANGES[currentStatus];
                  return (
                    <div key={skill.key} className="border border-border/40 p-3 sm:p-4 rounded-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
                        <p className="font-medium">{skill.label}</p>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                          <div className="flex flex-wrap gap-2">
                            {['Focus', 'Developing', 'Proficient'].map((status) => (
                              <button
                                key={status}
                                onClick={() => updateSkillStatus(skill.key, status)}
                                className={`px-2 sm:px-3 py-1 text-xs rounded-sm uppercase ${
                                  currentStatus === status
                                    ? status === 'Focus' ? 'bg-red-500/20 text-red-400'
                                    : status === 'Developing' ? 'bg-yellow-500/20 text-yellow-400'
                                    : 'bg-green-500/20 text-green-400'
                                    : 'bg-muted hover:bg-muted/80'
                                }`}
                              >
                                {status}
                              </button>
                            ))}
                          </div>
                          {currentStatus && range && (
                            <SkillScoreStepper
                              skillKey={skill.key}
                              value={techDev[skill.key]?.score || getDefaultScoreForLevel(currentStatus)}
                              min={range.min}
                              max={range.max}
                              rangeLabel={range.label}
                              onCommit={(k, v) => updateSkillScore(k, v)}
                              dataTestId={`score-input-${skill.key}`}
                            />
                          )}
                        </div>
                      </div>
                      <Textarea
                        placeholder="Coach notes..."
                        value={techDev[skill.key]?.notes || ''}
                        onChange={(e) => setTechDev({...techDev, [skill.key]: {...techDev[skill.key], notes: e.target.value}})}
                        className="bg-transparent w-full"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            <Button onClick={() => saveTechDev(false)} disabled={saving} className="bg-primary text-black">
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save Development'}
            </Button>

            {/* Player Goals Section - Athlete/Parent Self-Assessment */}
            <div className="mt-8 pt-6 border-t border-border/40">
              <CoachAthleteGoals athleteId={athleteId} getAuthHeaders={getAuthHeaders} />
            </div>
          </div>
        )}

        {activeTab === 'profile' && (
          <div className="space-y-6">
            <h2 className="font-heading text-xl uppercase">Player Development Profile</h2>
            <p className="text-muted-foreground text-sm">
              Compiled profile based on evaluations from Private Coach and Team Coach with weighted metrics.
            </p>
            <PlayerProfileView 
              athleteId={athleteId}
              athleteName={athlete?.name}
              showRadar={true}
            />
          </div>
        )}

        {activeTab === 'history' && (
          <div className="space-y-6" data-testid="athlete-dev-history-tab">
            <DevelopmentHistory athleteId={athleteId} athleteName={athlete?.name} />
          </div>
        )}

        {activeTab === 'training' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">Training Plan</h2>
              <Link to="/coach/training-plans">
                <Button variant="outline" size="sm">Manage Plans</Button>
              </Link>
            </div>
            {trainingPlan?.id ? (
              <div className="space-y-6">
                {/* Plan Overview */}
                <div className="bg-card border border-border/40 p-6 rounded-sm">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-heading text-lg">{trainingPlan.name || 'Training Plan'}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{trainingPlan.plan_type} Plan</span>
                        <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded capitalize">{trainingPlan.sport}</span>
                      </div>
                    </div>
                    {(trainingPlan.drills_full?.length > 0 || trainingPlan.drills?.length > 0) && (
                      <div className="text-right">
                        <p className="text-2xl font-heading text-primary">
                          {trainingPlan.drills_full?.length || trainingPlan.drills?.length || 0}
                        </p>
                        <p className="text-xs text-muted-foreground">Drills</p>
                      </div>
                    )}
                  </div>
                  
                  {trainingPlan.objectives?.length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-primary mb-2">Training Objectives</h4>
                      <ul className="space-y-2">
                        {trainingPlan.objectives.map((obj, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Target className="text-primary mt-0.5 flex-shrink-0" size={16} />
                            <span className="text-sm">{obj}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {trainingPlan.notes && (
                    <div className="bg-muted/30 p-4 rounded-sm">
                      <p className="text-sm font-medium text-primary mb-1">Coach Notes</p>
                      <p className="text-sm text-muted-foreground italic">"{trainingPlan.notes}"</p>
                    </div>
                  )}
                </div>

                {/* Drills Section */}
                {(trainingPlan.drills_full?.length > 0 || trainingPlan.drills?.length > 0) && (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <ClipboardList className="text-primary" size={20} />
                      <h3 className="font-heading text-lg">Drills</h3>
                    </div>
                    
                    <div className="space-y-3">
                      {(trainingPlan.drills_full || trainingPlan.drills || []).map((drill, idx) => (
                        <div 
                          key={drill.id || idx} 
                          className="bg-card border border-border/40 rounded-sm p-4"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-lg">
                                  {drill.category === 'technical' ? '⚽' : 
                                   drill.category === 'tactical' ? '📋' : 
                                   drill.category === 'physical' ? '💪' : '🎯'}
                                </span>
                                <span className="font-medium">{drill.name}</span>
                                {drill.video_url && (
                                  <span className="flex items-center gap-1 text-xs text-primary bg-primary/10 px-2 py-0.5 rounded">
                                    <Video size={10} />
                                    Video
                                  </span>
                                )}
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  drill.difficulty === 'beginner' ? 'bg-green-500/20 text-green-400' :
                                  drill.difficulty === 'intermediate' ? 'bg-yellow-500/20 text-yellow-400' :
                                  'bg-red-500/20 text-red-400'
                                }`}>
                                  {drill.difficulty}
                                </span>
                                <span className="text-xs px-2 py-0.5 bg-muted rounded capitalize">{drill.category}</span>
                                <span className="text-xs text-muted-foreground">{drill.duration}</span>
                              </div>
                              {drill.description && (
                                <p className="text-sm text-muted-foreground line-clamp-2">{drill.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No training plan created yet</p>
                <Link to="/coach/training-plans">
                  <Button>Create Training Plan</Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {activeTab === 'videos' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">Video Analysis</h2>
              <Link to="/coach/videos">
                <Button variant="outline" size="sm">Manage Videos</Button>
              </Link>
            </div>
            {videos.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No videos added yet</p>
                <Link to="/coach/videos">
                  <Button>Add Video</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {videos.map((video) => (
                  <div key={video.id} className="border border-border/40 p-4 rounded-sm">
                    <p className="font-medium">{video.title}</p>
                    <p className="text-sm text-muted-foreground capitalize">{video.video_type.replace('_', ' ')}</p>
                    <a href={video.url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline">Watch Video</a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'sessions' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl uppercase">Session History</h2>
              <div className="flex items-center gap-4">
                <div className="text-sm">
                  <span className="text-muted-foreground">Sessions: </span>
                  <span className="font-semibold text-primary">{athlete.sessions_remaining}/{athlete.sessions_total || '-'}</span>
                </div>
                <Link to="/coach/sessions">
                  <Button variant="outline" size="sm">Manage Sessions</Button>
                </Link>
              </div>
            </div>
            {sessions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground mb-4">No sessions recorded yet</p>
                <Link to="/coach/sessions">
                  <Button>Log Session</Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.slice(0, 10).map((session) => (
                  <div key={session.id} className="border border-border/40 p-4 rounded-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium capitalize">{session.session_type} Session</p>
                          <span className={`text-xs px-2 py-0.5 rounded capitalize whitespace-nowrap ${
                            session.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                            session.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            session.status === 'rescheduled' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {session.status || 'scheduled'}
                          </span>
                          {session.is_addon && (
                            <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 whitespace-nowrap">
                              Add-on {session.addon_value ? `$${session.addon_value}` : ''}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {new Date(session.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            hour: session.date?.includes('T') ? '2-digit' : undefined,
                            minute: session.date?.includes('T') ? '2-digit' : undefined
                          })}
                        </p>
                        {session.notes && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{session.notes}</p>}
                      </div>
                      {session.status !== 'completed' && (
                        <div className="self-end sm:self-center">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/sessions/${session.id}`, { status: 'completed' }, { headers: getAuthHeaders() });
                                toast.success('Session marked as completed');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to update session');
                              }
                            }}
                            className="text-green-400 hover:text-green-300 border-green-500/30 hover:bg-green-500/10"
                            data-testid={`complete-session-${session.id}`}
                          >
                            <Check size={14} className="mr-1" /> Complete
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Consistency Check Modal */}
      {showConsistencyModal && consistencyAlerts.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="text-amber-400" size={24} />
                <h3 className="font-heading text-lg">Consistency Check</h3>
              </div>
              <button onClick={() => setShowConsistencyModal(false)} className="text-muted-foreground hover:text-foreground">
                <X size={20} />
              </button>
            </div>
            
            <p className="text-sm text-muted-foreground mb-4">
              We detected {consistencyAlerts.length} potential inconsistencies in your evaluation. 
              Please review before saving.
            </p>
            
            <div className="space-y-3 max-h-64 overflow-y-auto mb-4">
              {consistencyAlerts.map((alert, idx) => (
                <div key={idx} className="bg-amber-500/10 border border-amber-500/20 rounded p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      alert.skill1Status === 'Focus' ? 'bg-red-500/20 text-red-400' :
                      alert.skill1Status === 'Developing' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {alert.skill1}: {alert.skill1Status}
                    </span>
                    <span className="text-muted-foreground">vs</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      alert.skill2Status === 'Focus' ? 'bg-red-500/20 text-red-400' :
                      alert.skill2Status === 'Developing' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {alert.skill2}: {alert.skill2Status}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">{alert.message}</p>
                </div>
              ))}
            </div>
            
            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setShowConsistencyModal(false)}>
                Review Evaluation
              </Button>
              <Button 
                onClick={() => saveTechDev(true)} 
                disabled={saving}
                className="bg-primary text-black"
              >
                {saving ? 'Saving...' : 'Save Anyway'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachAthleteDetail;
